// Vuelette analytics — tunn wrapper kring Umamis global `umami.track()`.
//
// Skripten laddas i index.html. Här definierar vi typade event-namn så att
// vi inte staver fel i call sites, och en send() som tysta-failar om
// tracker inte hunnit ladda (eller är blockerad av adblocker).
//
// Custom events vi mäter idag:
//
//   Marketing / landing
//   - landing_view              { page: 'hub' | 'models' | 'photographers' | 'duos' }
//   - signin_click              {}
//   - request_access_click      { source: page }
//   - audience_card_click       { target: 'models' | 'photographers' | 'duos' }
//
//   Auth
//   - magic_link_requested      { email_domain: string }
//   - magic_link_consumed       { result: 'success' | 'expired' | 'invalid' }
//
//   Produkt (in-app)
//   - shoot_opened              { shoot_id: string }
//   - set_opened                { set_id: string }
//   - image_rated               { stars: number }
//   - image_favorited           {}
//   - image_added_to_set        {}
//   - set_created               {}
//
//   Portfolios (när vi bygger dem)
//   - portfolio_view            { portfolio_id: string }
//   - portfolio_image_view      { portfolio_id: string; image_id: string }
//   - portfolio_contact_click   { portfolio_id: string; channel: 'email' | 'phone' | 'instagram' }
//   - portfolio_scroll_depth    { portfolio_id: string; depth: 0.25|0.5|0.75|1.0 }

export type AnalyticsEvent =
  // Landing
  | { name: 'landing_view'; data: { page: LandingPage } }
  | { name: 'signin_click'; data?: undefined }
  | { name: 'request_access_click'; data: { source: LandingPage | 'app' } }
  | { name: 'audience_card_click'; data: { target: LandingPage } }
  // Auth
  | { name: 'magic_link_requested'; data: { email_domain: string } }
  | { name: 'magic_link_consumed'; data: { result: 'success' | 'expired' | 'invalid' } }
  // Produkt
  | { name: 'shoot_opened'; data: { shoot_id: string } }
  | { name: 'set_opened'; data: { set_id: string } }
  | { name: 'image_rated'; data: { stars: number } }
  | { name: 'image_favorited'; data?: undefined }
  | { name: 'image_added_to_set'; data?: undefined }
  | { name: 'set_created'; data?: undefined }
  // Portfolios
  | { name: 'portfolio_view'; data: { portfolio_id: string } }
  | { name: 'portfolio_image_view'; data: { portfolio_id: string; image_id: string } }
  | {
      name: 'portfolio_contact_click';
      data: { portfolio_id: string; channel: 'email' | 'phone' | 'instagram' };
    }
  | {
      name: 'portfolio_scroll_depth';
      data: { portfolio_id: string; depth: 0.25 | 0.5 | 0.75 | 1.0 };
    };

export type LandingPage = 'hub' | 'models' | 'photographers' | 'duos';

interface UmamiGlobal {
  track: (name: string, data?: Record<string, unknown>) => void;
}

declare global {
  interface Window {
    umami?: UmamiGlobal;
  }
}

/**
 * Skicka ett analytics-event. Tyst-failar om Umami inte är laddad eller
 * blockerad. Vi vill ALDRIG att tracking ska bryta UI-flöden.
 */
export function track(event: AnalyticsEvent): void {
  if (typeof window === 'undefined') return;
  const umami = window.umami;
  if (!umami || typeof umami.track !== 'function') return;
  try {
    if (event.data) {
      umami.track(event.name, event.data as Record<string, unknown>);
    } else {
      umami.track(event.name);
    }
  } catch {
    // Tracker-fel ska aldrig propagera till UI.
  }
}

/**
 * Owner-aware variant av track() för portfolio-events. Skickar INTE event om
 * besökaren är inloggad workspace-medlem (då är det Henrik/Michelle som
 * granskar sin egen portfolio — den trafiken ska inte förorena statistiken).
 */
export function trackPortfolio(event: AnalyticsEvent, isOwner: boolean): void {
  if (isOwner) return;
  track(event);
}

/**
 * Helper för att extrahera bara emailens domän — vi vill INTE skicka hela
 * adressen till analytics av integritetsskäl. Domänen räcker för att se
 * "många från gmail.com testar Vuelette".
 */
export function emailDomain(email: string): string {
  const at = email.lastIndexOf('@');
  if (at < 0) return 'unknown';
  return email.slice(at + 1).toLowerCase();
}
